import { create } from 'zustand'
import { api, SessionUser } from '../services/api'
import { getDataSnapshot, useTaskStore } from './taskStore'
import { outbox } from '../services/outbox'

interface AuthState {
  user: SessionUser | null
  loading: boolean
  error: string | null
  guestSnapshot: ReturnType<typeof getDataSnapshot> | null
  needsImportDecision: boolean
  syncDeferred: boolean
  initialize: () => Promise<void>
  register: (name: string, email: string, password: string) => Promise<boolean>
  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  finishImportDecision: (defer?: boolean) => void
}

const hasGuestData = (snapshot: ReturnType<typeof getDataSnapshot>) =>
  snapshot.tasks.length + snapshot.projects.length + snapshot.links.length + snapshot.diaryEntries.length + snapshot.notes.length > 0 ||
  snapshot.pages.some((page) => page.canvasWidgets.length > 0)

function importStateFor(user: SessionUser, snapshot: ReturnType<typeof getDataSnapshot>) {
  const owner = localStorage.getItem('taskpriority_data_owner')
  return owner !== user.id && hasGuestData(snapshot)
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null, loading: true, error: null, guestSnapshot: null, needsImportDecision: false, syncDeferred: false,
  initialize: async () => {
    try {
      const { user } = await api<{ user: SessionUser | null }>('/api/auth/session')
      const guestSnapshot = getDataSnapshot()
      set({ user, loading: false, guestSnapshot: user && importStateFor(user, guestSnapshot) ? guestSnapshot : null, needsImportDecision: Boolean(user && importStateFor(user, guestSnapshot)), syncDeferred: false })
    } catch {
      set({ loading: false, user: null })
    }
  },
  register: async (name, email, password) => {
    set({ loading: true, error: null })
    const guestSnapshot = getDataSnapshot()
    try {
      const { user } = await api<{ user: SessionUser }>('/api/auth/register', { method: 'POST', body: JSON.stringify({ name, email, password }) })
      const needsImportDecision = hasGuestData(guestSnapshot)
      set({ user, loading: false, guestSnapshot: needsImportDecision ? guestSnapshot : null, needsImportDecision, syncDeferred: false })
      return true
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : 'Não foi possível criar a conta.' })
      return false
    }
  },
  login: async (email, password) => {
    set({ loading: true, error: null })
    const guestSnapshot = getDataSnapshot()
    try {
      const { user } = await api<{ user: SessionUser }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) })
      const needsImportDecision = importStateFor(user, guestSnapshot)
      set({ user, loading: false, guestSnapshot: needsImportDecision ? guestSnapshot : null, needsImportDecision, syncDeferred: false })
      return true
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : 'E-mail ou senha inválidos.' })
      return false
    }
  },
  logout: async () => {
    try { await api('/api/auth/logout', { method: 'POST' }) } finally {
      localStorage.removeItem('taskpriority_data_owner')
      await outbox.clear().catch(() => undefined)
      useTaskStore.getState().clearUserData()
      set({ user: null, error: null, guestSnapshot: null, needsImportDecision: false, syncDeferred: false })
    }
  },
  finishImportDecision: (defer = false) => set({ guestSnapshot: null, needsImportDecision: false, syncDeferred: defer }),
}))
